/**
 * InstallHintBanner — sticky emerald top banner on mobile that nudges
 * new users to install Lumfi as a PWA.
 *
 * Visibility rules (all must be true):
 *   • Viewport is mobile (< lg breakpoint, controlled via Tailwind
 *     `lg:hidden`).
 *   • The app isn't already running in standalone (installed PWA).
 *   • The user hasn't dismissed the banner before
 *     (`lumfi.install_hint.dismissed` in localStorage).
 *
 * When the user dismisses we persist the choice forever — the hint is
 * meant to be a one-time nudge, not a recurring nag. Users who want to
 * install later still have:
 *   • The InstallAppButton in the MobileTopBar (always visible).
 *   • The "Instalar app" entry in the More dropdown.
 *   • The /help page with a big banner.
 */
import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useLang } from "../../contexts/LangContext";

const STORAGE_KEY = "lumfi.install_hint.dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true;
}

export function InstallHintBanner() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* ignore */ }
    setVisible(true);
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        dismiss();
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: send the user to /help where there's a big install
      // banner with iOS instructions.
      window.location.href = "/help";
    }
  };

  return (
    <div
      data-testid="install-hint-banner"
      // lg:hidden = only on mobile. Non-sticky so it scrolls away with
      // content — the MobileTopBar (sticky underneath) keeps the
      // InstallAppButton + switchers visible at all times, which is
      // the right escalation path if the user dismisses this nudge by
      // scrolling past it.
      className="lg:hidden bg-emerald-600 text-white px-3 py-2 flex items-center gap-2 text-[12px] shadow-md"
    >
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={handleInstall}
        data-testid="install-hint-cta"
        className="flex items-center gap-2 flex-1 text-left active:opacity-80"
      >
        <span aria-hidden className="text-base">💚</span>
        <span className="font-semibold">
          {isEs ? "Instala Lumfi como app" : "Install Lumfi as an app"}
        </span>
        <span className="hidden sm:inline opacity-90">
          · {isEs ? "un click" : "one tap"}
        </span>
        <Download className="h-3.5 w-3.5 ml-auto opacity-90" />
      </button>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={dismiss}
        data-testid="install-hint-dismiss"
        aria-label={isEs ? "Cerrar" : "Dismiss"}
        className="h-6 w-6 rounded-full hover:bg-emerald-700 flex items-center justify-center shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
